import { Component, AfterViewInit, ElementRef, ViewChild, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { BrandingConfigurationService } from '@ptl/brand';
import { FormControl, Validators } from '@angular/forms';

interface CityData {
  name: string;
  stateName: string;
  zipCode4: string;
  stateAbbreviation: string;
  zipCode5: string;
}

interface CityResponse {
  cityData: CityData[];
}

@FormComponentLookup('autocomplete-input-addresses')
@Component({
  selector: 'ptl-autocomplete-input-addresses',
  templateUrl: './autocomplete-input-addresses.component.html',
  styleUrls: ['./autocomplete-input-addresses.component.scss'],
  standalone: false
})
export class AutoCompleteInputAddressesComponent extends FormEntryComponentClass implements OnInit, AfterViewInit {
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  loadingCities = false;
  errorLoadingCities = '';
  private citiesSubject = new BehaviorSubject<string[]>([]);
  cities$ = this.citiesSubject.asObservable();
  filteredCities: Observable<string[]>;
  private isInitialized = false;
  private hasEventBindings = false;

  /**
   * Separate ViewChild setter that watches for #entryRef appearing in the DOM.
   * When display changes from false to true, Angular re-renders the @if block
   * and this setter fires with the new ElementRef, assigning it to the inherited
   * entryRef property and triggering setEventBindings.
   */
  @ViewChild('entryRef') set entryRefSetter(ref: ElementRef) {
    this.entryRef = ref;
    if (ref && !this.hasEventBindings) {
      this.hasEventBindings = true;
      this.onEntryRefAvailable();
    }
  }

  constructor(
    private httpClient: HTTPWrapperContext,
    private brandService: BrandingConfigurationService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    // Only initialize if display is true
    if (this.entry?.base?.display) {
      this.initializeField();
    }
  }

  /**
   * Called when entryRef becomes available in the DOM (display changed to true).
   * Triggers setEventBindings from FormEntryComponentClass and initializes the field.
   */
  private onEntryRefAvailable(): void {
    // Trigger the base class event bindings that depend on entryRef
    this.setEventBindings();

    if (!this.isInitialized) {
      this.initializeField();
    }

    this.cdr.detectChanges();

    setTimeout(() => {
      if (this.entry?.formControlAttributes?.formControlName && this.entryRef) {
        const currentValue = this.formControls[this.entry.formControlAttributes.formControlName].value;
        if (currentValue) {
          this.formControls[this.entry.formControlAttributes.formControlName].setValue(currentValue);
        }
      }
    }, 800);
  }

  private initializeField(): void {
    if (this.isInitialized) {
      return;
    }

    const zipCode = this.ngRedux.getState().componentStates.ZipCounty.zipCode;
    if (zipCode) {
      this.getCitiesForZip(zipCode);
    }

    this.filteredCities = this.formControls[this.entry.formControlAttributes.formControlName].valueChanges.pipe(
      startWith(''),
      map(value => this.filterCities(value || ''))
    );

    this.isInitialized = true;
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  public getPatternErrorMessage(): string {
    const patternConfig = this.entry?.formControlAttributes?.validators?.find(
      v => v.type === 'pattern'
    );
    return patternConfig?.message || 'Pattern must match';
  }

  private filterCities(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.citiesSubject.value.filter(city => city.toLowerCase().includes(filterValue));
  }

  private autoPopulateCity(cityName: string): void {
    const currentValue = this.formControls[this.entry.formControlAttributes.formControlName].value;
    const shouldPopulate = !currentValue ||
      !this.citiesSubject.value.includes(currentValue);

    if (shouldPopulate) {
      this.formControls[this.entry.formControlAttributes.formControlName].setValue(cityName, { emitEvent: true });
    }
  }

  private getCitiesForZip(zipCode: string): void {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/sxesvc/api/v2/zipCode/city?zipCode=${zipCode}`,
      strategyConfigs: { forceModernUrl: true },
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
      }),
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);

    from(this.httpClient.makeRequest(requestConfig)).pipe(
      map((response: CityResponse) => {
        return response.cityData.map(city => city.name);
      }),
      tap(cities => {
        if (cities.length === 1) {
          this.autoPopulateCity(cities[0]);
        }
      }),
      catchError(error => {
        console.error('Error fetching cities:', error);
        this.errorLoadingCities = 'Failed to fetch cities';
        return of([]);
      })
    ).subscribe(cities => {
      this.citiesSubject.next(cities);

      const uniqueCities = [...new Set(cities)];
      if (uniqueCities.length === 1 && !this.formControls[this.entry.formControlAttributes.formControlName].value) {
        this.autoPopulateCity(uniqueCities[0]);
      }
    });
  }
// Dummy changes for the cityfield
}



